import { Game, GameType, GradeLevel } from '../types';
import { parseDelimitedText, rowsToObjects } from './delimitedImport';

/**
 * Chuẩn hóa bản ghi trò chơi từ API / localStorage (snake_case, content JSON string, nested `data`).
 */
export function normalizeGameRecord(raw: unknown): Game | null {
  if (!raw || typeof raw !== 'object') return null;

  const r = raw as Record<string, unknown>;
  const nested =
    r.data && typeof r.data === 'object' && !Array.isArray(r.data)
      ? (r.data as Record<string, unknown>)
      : null;
  const item =
    nested && (nested.content !== undefined || nested.title !== undefined)
      ? { ...r, ...nested }
      : r;

  const id = item.id ?? item._id;
  if (id == null) return null;

  let content: unknown = item.content;
  for (let i = 0; i < 2 && typeof content === 'string'; i++) {
    try {
      content = JSON.parse(content as string);
    } catch {
      break;
    }
  }
  if (content == null) {
    content = {};
  }

  let settings: unknown = item.settings ?? {};
  if (typeof settings === 'string') {
    try {
      settings = JSON.parse(settings);
    } catch {
      settings = {};
    }
  }

  const gameType = (item.gameType ?? item.game_type) as GameType | undefined;
  const gradeLevel = (item.gradeLevel ?? item.grade_level) as GradeLevel | undefined;

  return {
    ...(item as object),
    id: String(id),
    title: String(item.title ?? ''),
    topic: String(item.topic ?? ''),
    gradeLevel: gradeLevel || GradeLevel.TIEU_HOC,
    gameType: gameType || GameType.DO_VUI_LINH_HOAT,
    content,
    settings: (typeof settings === 'object' && settings !== null ? settings : {}) as Game['settings'],
    savedDate: String(item.savedDate ?? item.saved_date ?? ''),
    buildMode: (item.buildMode ?? item.build_mode) as Game['buildMode'],
    sourceDocumentId: (item.sourceDocumentId ?? item.source_document_id) as string | undefined,
  } as Game;
}

export type ParsedGameImport = {
  content: any;
  importedCount: number;
  summaryLabel: string;
};

const QUESTION_BASED_GAME_TYPES = new Set<GameType>([
  GameType.DO_VUI_LINH_HOAT,
  GameType.AI_NHANH_HON,
  GameType.AI_LA_TRIEU_PHU_MINI,
  GameType.TRUY_TIM_KHO_BAU,
  GameType.TIEP_SUC,
  GameType.TIM_MAU_TIM_HINH,
  GameType.VONG_QUAY_AM_NHAC,
  GameType.DAU_BOSS_CUOI_BAI,   // dùng questions[]
  GameType.WORDLE_MON_HOC,      // dùng words[] (import tương tự)
]);

const SCENARIO_GAME_TYPES = new Set<GameType>([
  GameType.DONG_VAI_TINH_HUONG,
  GameType.CASE_STUDY_NHOM,
  GameType.CASE_STUDY_NHO,
  GameType.AI_LA_NHA_KHOA_HOC_TRE,
  GameType.DU_AN_MINI,
  GameType.PEER_TEACHING,
]);

const DEBATE_GAME_TYPES = new Set<GameType>([
  GameType.TRANH_BIEN_NHANH,
  GameType.DEBATE,
]);

export type GameValidationResult = {
  message: string;
  itemIndex?: number;
};

export type GameConfigurableSetting = {
  key: string;
  label: string;
  type: 'number' | 'text' | 'select' | 'boolean';
  defaultValue: any;
  options?: Array<{ value: string | number | boolean; label: string }>;
};

export type GameSettingBounds = {
  min: number;
  max: number;
  reason?: string;
};

export function getGameContentItemCount(game: Pick<Game, 'gameType' | 'content'> | null | undefined): number {
  if (!game) return 0;
  const content: any = game.content;
  if (!content) return 0;

  if (QUESTION_BASED_GAME_TYPES.has(game.gameType)) {
    const questions = Array.isArray(content) ? content : content.questions;
    return Array.isArray(questions) ? questions.length : 0;
  }

  if (game.gameType === GameType.GHEP_DOI_THONG_TIN) {
    const pairs = Array.isArray(content) ? content : content.matchingPairs;
    return Array.isArray(pairs) ? pairs.length : 0;
  }

  if (game.gameType === GameType.BINGO_KIEN_THUC) {
    const items = Array.isArray(content) ? content : content.bingoItems;
    return Array.isArray(items) ? items.length : 0;
  }

  if (game.gameType === GameType.DONG_THOI_GIAN) {
    const events = Array.isArray(content) ? content : content.events;
    return Array.isArray(events) ? events.length : 0;
  }

  if (game.gameType === GameType.O_CHU_BI_MAT || game.gameType === GameType.GIAI_MA_O_CHU) {
    const clues = content?.crossword?.clues || content?.clues || content?.questions;
    return Array.isArray(clues) ? clues.length : 0;
  }

  if (SCENARIO_GAME_TYPES.has(game.gameType)) {
    const scenarios = Array.isArray(content) ? content : content.scenarios;
    return Array.isArray(scenarios) ? scenarios.length : 0;
  }

  if (DEBATE_GAME_TYPES.has(game.gameType)) {
    const topics = Array.isArray(content) ? content : content.debateTopics;
    return Array.isArray(topics) ? topics.length : 0;
  }

  if (game.gameType === GameType.BE_BAT_CHUOC) {
    const actions = Array.isArray(content) ? content : content.actions;
    return Array.isArray(actions) ? actions.length : 0;
  }

  if (game.gameType === GameType.GHEP_HINH_GHEP_CHU) {
    const pairs = Array.isArray(content) ? content : content.pairs;
    return Array.isArray(pairs) ? pairs.length : 0;
  }

  if (game.gameType === GameType.PHAN_LOAI_NHOM) {
    const cards = content?.classification?.cards;
    return Array.isArray(cards) ? cards.length : 0;
  }

  if (game.gameType === GameType.DUNG_SAI) {
    const items = Array.isArray(content) ? content : content.trueFalseStatements;
    return Array.isArray(items) ? items.length : 0;
  }

  if (game.gameType === GameType.DIEN_KHUYET) {
    const items = Array.isArray(content) ? content : content.fillInBlanks;
    return Array.isArray(items) ? items.length : 0;
  }

  if (game.gameType === GameType.LAT_THE_TIM_CAP) {
    const items = Array.isArray(content) ? content : content.memoryPairs;
    return Array.isArray(items) ? items.length : 0;
  }

  if (game.gameType === GameType.SAP_XEP_THU_TU) {
    const items = content?.orderingSequence?.items;
    return Array.isArray(items) ? items.length : 0;
  }

  if (game.gameType === GameType.TIM_TU_AN) {
    const words = content?.wordSearch?.words;
    return Array.isArray(words) ? words.length : 0;
  }

  return 0;
}

/**
 * Các setting key điều khiển số lượng item sẽ được dùng từ nội dung game.
 * Giá trị nên được giới hạn bởi số item thực tế.
 */
const CONTENT_BOUND_SETTING_KEYS = new Set<string>([
  'numOfQuestions',
  'questionsPerGame',
  'totalQuestions',
  'totalStations',
  'totalRounds',
]);

const PERCENT_SETTING_KEYS = new Set<string>([
  'passThreshold',
  'winThreshold',
  'winConditionPercentage',
]);

export function getSettingBounds(
  setting: GameConfigurableSetting,
  contentItemCount: number
): GameSettingBounds | null {
  if (setting.type !== 'number') return null;

  if (PERCENT_SETTING_KEYS.has(setting.key) || /percentage|percent|threshold/i.test(setting.key)) {
    return { min: 0, max: 100, reason: 'Giá trị phần trăm hợp lệ từ 0 đến 100.' };
  }

  if (CONTENT_BOUND_SETTING_KEYS.has(setting.key)) {
    const max = contentItemCount > 0 ? contentItemCount : 1;
    return {
      min: 1,
      max,
      reason:
        contentItemCount > 0
          ? `Không thể vượt quá ${contentItemCount} mục đang có trong nội dung trò chơi.`
          : 'Hãy thêm nội dung trò chơi trước khi tăng giá trị này.',
    };
  }

  if (/team|player|group|doi/i.test(setting.key)) {
    return { min: 1, max: 20, reason: 'Số lượng đội/người chơi hợp lý từ 1 đến 20.' };
  }

  if (/score|point|diem/i.test(setting.key)) {
    return { min: 0, max: 10000 };
  }

  if (/seconds|giay/i.test(setting.key)) {
    return { min: 1, max: 3600, reason: 'Thời lượng hợp lệ từ 1 đến 3600 giây.' };
  }

  if (/minutes|minute|phut/i.test(setting.key)) {
    return { min: 1, max: 180, reason: 'Thời lượng hợp lệ từ 1 đến 180 phút.' };
  }

  if (/time|duration/i.test(setting.key)) {
    return { min: 1, max: 3600 };
  }

  return { min: 0, max: 99999 };
}

/**
 * Chuẩn hóa giá trị setting theo type + bounds.
 * Giữ nguyên fallback `defaultValue` khi giá trị không hợp lệ.
 */
export function sanitizeSettingValue(
  setting: GameConfigurableSetting,
  rawValue: unknown,
  contentItemCount: number
): any {
  if (setting.type === 'number') {
    const bounds = getSettingBounds(setting, contentItemCount);
    const parsed = typeof rawValue === 'number' ? rawValue : parseFloat(String(rawValue ?? ''));
    if (!Number.isFinite(parsed)) {
      return Number(setting.defaultValue) || (bounds?.min ?? 0);
    }
    const rounded = Math.round(parsed);
    if (!bounds) return rounded;
    return Math.min(bounds.max, Math.max(bounds.min, rounded));
  }

  if (setting.type === 'select') {
    if (!setting.options || setting.options.length === 0) return rawValue ?? setting.defaultValue;
    const match = setting.options.find(
      (opt) => String(opt.value) === String(rawValue ?? '')
    );
    return match ? match.value : setting.defaultValue;
  }

  if (setting.type === 'boolean') {
    if (typeof rawValue === 'boolean') return rawValue;
    if (rawValue === 'true') return true;
    if (rawValue === 'false') return false;
    return Boolean(setting.defaultValue);
  }

  // text
  return typeof rawValue === 'string' ? rawValue : String(rawValue ?? setting.defaultValue ?? '');
}

export function buildDefaultSettings(
  settings: GameConfigurableSetting[] | undefined,
  existing?: Record<string, any>,
  contentItemCount = 0
): Record<string, any> {
  if (!settings || settings.length === 0) return { ...(existing || {}) };
  const result: Record<string, any> = {};
  settings.forEach((setting) => {
    const raw = existing && existing[setting.key] !== undefined ? existing[setting.key] : setting.defaultValue;
    result[setting.key] = sanitizeSettingValue(setting, raw, contentItemCount);
  });
  return { ...(existing || {}), ...result };
}

const normalizeText = (value: unknown): string => String(value ?? '').trim();

const isNonEmptyArray = (value: unknown): value is any[] =>
  Array.isArray(value) && value.length > 0;

const findDuplicateText = (values: string[]): string | null => {
  const seen = new Set<string>();
  for (const value of values) {
    const normalized = value.toLowerCase();
    if (!normalized) continue;
    if (seen.has(normalized)) return value;
    seen.add(normalized);
  }
  return null;
};

const validateQuestionBased = (questions: any[]): GameValidationResult | null => {
  if (!isNonEmptyArray(questions)) {
    return { message: 'Trò chơi phải có ít nhất một câu hỏi.' };
  }
  for (let i = 0; i < questions.length; i++) {
    const q = questions[i] || {};
    const questionText = normalizeText(q.question);
    const answerText = normalizeText(q.answer);
    if (!questionText) {
      return { message: `Câu hỏi số ${i + 1} chưa có nội dung câu hỏi.`, itemIndex: i };
    }
    if (!answerText) {
      return { message: `Câu hỏi số ${i + 1} chưa có đáp án đúng.`, itemIndex: i };
    }
    if (Array.isArray(q.options) && q.options.length > 0) {
      const normalizedOptions = q.options.map((o: unknown) => normalizeText(o));
      const nonEmpty = normalizedOptions.filter(Boolean);
      if (nonEmpty.length < 2) {
        return { message: `Câu hỏi số ${i + 1} cần ít nhất 2 đáp án không bỏ trống.`, itemIndex: i };
      }
      const duplicate = findDuplicateText(nonEmpty);
      if (duplicate) {
        return { message: `Câu hỏi số ${i + 1} có đáp án bị trùng: "${duplicate}".`, itemIndex: i };
      }
      const isAnswerInOptions = nonEmpty.some(
        (opt) => opt.toLowerCase() === answerText.toLowerCase()
      );
      if (!isAnswerInOptions) {
        return {
          message: `Đáp án đúng của câu hỏi số ${i + 1} phải trùng với một trong các phương án đã nhập.`,
          itemIndex: i,
        };
      }
    }
  }
  return null;
};

const validateMatchingPairs = (pairs: any[]): GameValidationResult | null => {
  if (!isNonEmptyArray(pairs)) {
    return { message: 'Trò chơi ghép đôi phải có ít nhất một cặp thông tin.' };
  }
  const leftValues: string[] = [];
  const rightValues: string[] = [];
  for (let i = 0; i < pairs.length; i++) {
    const itemA = normalizeText(pairs[i]?.itemA);
    const itemB = normalizeText(pairs[i]?.itemB);
    if (!itemA || !itemB) {
      return { message: `Cặp ghép số ${i + 1} phải có đủ cả hai vế.`, itemIndex: i };
    }
    leftValues.push(itemA);
    rightValues.push(itemB);
  }
  const dupLeft = findDuplicateText(leftValues);
  if (dupLeft) return { message: `Vế A bị trùng: "${dupLeft}".` };
  const dupRight = findDuplicateText(rightValues);
  if (dupRight) return { message: `Vế B bị trùng: "${dupRight}".` };
  return null;
};

const validateBingoItems = (items: any[]): GameValidationResult | null => {
  if (!isNonEmptyArray(items)) {
    return { message: 'Bingo kiến thức phải có ít nhất một ô nội dung.' };
  }
  const texts: string[] = [];
  for (let i = 0; i < items.length; i++) {
    const text = normalizeText(items[i]?.text);
    if (!text) {
      return { message: `Ô Bingo số ${i + 1} không được để trống.`, itemIndex: i };
    }
    texts.push(text);
  }
  const dup = findDuplicateText(texts);
  if (dup) return { message: `Ô Bingo bị trùng: "${dup}".` };
  return null;
};

const validateTimelineEvents = (events: any[]): GameValidationResult | null => {
  if (!isNonEmptyArray(events)) {
    return { message: 'Dòng thời gian phải có ít nhất một sự kiện.' };
  }
  for (let i = 0; i < events.length; i++) {
    const title = normalizeText(events[i]?.title);
    const year = normalizeText(events[i]?.year);
    if (!title) {
      return { message: `Sự kiện số ${i + 1} chưa có tiêu đề.`, itemIndex: i };
    }
    if (!year) {
      return { message: `Sự kiện số ${i + 1} chưa có năm/thời điểm.`, itemIndex: i };
    }
  }
  return null;
};

const validateCrossword = (content: any): GameValidationResult | null => {
  const crossword = content?.crossword;
  const keyword = normalizeText(crossword?.keyword ?? content?.keyword);
  const rawClues: any[] = Array.isArray(crossword?.clues)
    ? crossword.clues
    : Array.isArray(content?.clues)
      ? content.clues
      : Array.isArray(content?.questions)
        ? content.questions
        : [];

  if (!keyword) {
    return { message: 'Ô chữ cần có từ khóa chính.' };
  }
  if (!isNonEmptyArray(rawClues)) {
    return { message: 'Ô chữ phải có ít nhất một gợi ý hàng ngang.' };
  }

  const answers: string[] = [];
  for (let i = 0; i < rawClues.length; i++) {
    const entry = rawClues[i] || {};
    const clue = normalizeText(entry.clue ?? entry.question ?? entry.text);
    const answer = normalizeText(entry.answer);
    if (!clue) {
      return { message: `Hàng ngang số ${i + 1} chưa có gợi ý.`, itemIndex: i };
    }
    if (!answer) {
      return { message: `Hàng ngang số ${i + 1} chưa có đáp án.`, itemIndex: i };
    }
    if (!answer.toUpperCase().includes(keyword.toUpperCase().slice(0, 1))) {
      // Non-blocking check: keyword slot should exist; fallback handled by template
    }
    const slot = Number(entry.keywordSlot);
    if (entry.keywordSlot !== undefined && entry.keywordSlot !== null && entry.keywordSlot !== '') {
      if (!Number.isInteger(slot) || slot < 1 || slot > answer.length) {
        return {
          message: `Vị trí khớp của hàng ngang số ${i + 1} phải là số nguyên từ 1 đến ${answer.length}.`,
          itemIndex: i,
        };
      }
    }
    answers.push(answer);
  }

  const dup = findDuplicateText(answers);
  if (dup) return { message: `Đáp án hàng ngang bị trùng: "${dup}".` };
  return null;
};

const validateScenarios = (scenarios: any[]): GameValidationResult | null => {
  if (!isNonEmptyArray(scenarios)) {
    return { message: 'Trò chơi cần có ít nhất một tình huống.' };
  }
  for (let i = 0; i < scenarios.length; i++) {
    const s = scenarios[i] || {};
    const title = normalizeText(s.title);
    const problem = normalizeText(s.problem ?? s.description);
    if (!title) {
      return { message: `Tình huống số ${i + 1} chưa có tiêu đề.`, itemIndex: i };
    }
    if (!problem) {
      return { message: `Tình huống số ${i + 1} chưa có mô tả vấn đề/bối cảnh.`, itemIndex: i };
    }
    const roles = Array.isArray(s.roles) ? s.roles : [];
    const roleNames = roles
      .map((r: any) => (typeof r === 'string' ? r : normalizeText(r?.name)))
      .filter(Boolean);
    if (roleNames.length === 0) {
      return { message: `Tình huống số ${i + 1} phải có ít nhất một vai diễn.`, itemIndex: i };
    }
    const settings = s.settings || {};
    for (const key of ['prepTime', 'playTime', 'feedbackTime'] as const) {
      const value = settings[key];
      if (value !== undefined && value !== null && value !== '') {
        const num = Number(value);
        if (!Number.isFinite(num) || num < 0 || num > 180) {
          return {
            message: `Thời lượng (${key}) của tình huống số ${i + 1} phải là số từ 0 đến 180 phút.`,
            itemIndex: i,
          };
        }
      }
    }
  }
  return null;
};

const validateDebateTopics = (topics: any[]): GameValidationResult | null => {
  if (!isNonEmptyArray(topics)) {
    return { message: 'Trò chơi tranh biện phải có ít nhất một chủ đề.' };
  }
  for (let i = 0; i < topics.length; i++) {
    const t = topics[i] || {};
    const topic = normalizeText(t.topic);
    if (!topic) {
      return { message: `Chủ đề tranh biện số ${i + 1} không được để trống.`, itemIndex: i };
    }
  }
  return null;
};

const validateActions = (actions: any[]): GameValidationResult | null => {
  if (!isNonEmptyArray(actions)) {
    return { message: 'Trò chơi cần có ít nhất một động tác.' };
  }
  for (let i = 0; i < actions.length; i++) {
    const a = actions[i] || {};
    const title = normalizeText(a.title);
    if (!title) {
      return { message: `Động tác số ${i + 1} chưa có tên.`, itemIndex: i };
    }
  }
  return null;
};

const validateClassification = (classification: any): GameValidationResult | null => {
  if (!classification || typeof classification !== 'object') {
    return { message: 'Trò chơi phân loại cần có dữ liệu cột & thẻ.' };
  }
  const columns = Array.isArray(classification.columns) ? classification.columns : [];
  const cards = Array.isArray(classification.cards) ? classification.cards : [];
  if (columns.length < 2) {
    return { message: 'Trò chơi phân loại phải có ít nhất 2 cột phân loại.' };
  }
  if (columns.length > 4) {
    return { message: 'Trò chơi phân loại tối đa 4 cột.' };
  }
  const columnIds = new Set<string>();
  for (let i = 0; i < columns.length; i++) {
    const id = normalizeText(columns[i]?.id);
    const label = normalizeText(columns[i]?.label);
    if (!id) {
      return { message: `Cột số ${i + 1} chưa có mã ID (vd: A, B, C).` };
    }
    if (columnIds.has(id.toLowerCase())) {
      return { message: `Mã ID cột bị trùng: "${id}".` };
    }
    columnIds.add(id.toLowerCase());
    if (!label) {
      return { message: `Cột số ${i + 1} chưa có tên hiển thị.` };
    }
  }
  if (cards.length < columns.length * 2) {
    return { message: `Cần ít nhất ${columns.length * 2} thẻ (mỗi cột nên có ≥ 2 thẻ).` };
  }
  for (let i = 0; i < cards.length; i++) {
    const c = cards[i] || {};
    const text = normalizeText(c.text);
    const col = normalizeText(c.correctColumn);
    if (!text) {
      return { message: `Thẻ số ${i + 1} chưa có nội dung.`, itemIndex: i };
    }
    if (!col) {
      return { message: `Thẻ số ${i + 1} chưa chọn cột đúng.`, itemIndex: i };
    }
    if (!columnIds.has(col.toLowerCase())) {
      return { message: `Thẻ số ${i + 1} đang chọn cột không tồn tại ("${col}").`, itemIndex: i };
    }
  }
  return null;
};

const validateTrueFalse = (items: any[]): GameValidationResult | null => {
  if (!isNonEmptyArray(items)) {
    return { message: 'Trò chơi Đúng - Sai phải có ít nhất một khẳng định.' };
  }
  for (let i = 0; i < items.length; i++) {
    const it = items[i] || {};
    const stmt = normalizeText(it.statement);
    if (!stmt) {
      return { message: `Khẳng định số ${i + 1} chưa có nội dung.`, itemIndex: i };
    }
    if (typeof it.isTrue !== 'boolean') {
      return { message: `Khẳng định số ${i + 1} chưa chọn Đúng hay Sai.`, itemIndex: i };
    }
  }
  return null;
};

const validateFillInBlanks = (items: any[]): GameValidationResult | null => {
  if (!isNonEmptyArray(items)) {
    return { message: 'Trò chơi Điền khuyết phải có ít nhất một câu.' };
  }
  for (let i = 0; i < items.length; i++) {
    const it = items[i] || {};
    const sentence = normalizeText(it.sentence);
    if (!sentence) {
      return { message: `Câu số ${i + 1} chưa có nội dung.`, itemIndex: i };
    }
    const blankCount = (sentence.match(/_{4,}/g) || []).length;
    if (blankCount === 0) {
      return { message: `Câu số ${i + 1} cần có ít nhất một ô trống "____" (4 gạch dưới).`, itemIndex: i };
    }
    const answers = Array.isArray(it.answers) ? it.answers.map((a: unknown) => normalizeText(a)) : [];
    if (answers.length !== blankCount) {
      return {
        message: `Câu số ${i + 1} có ${blankCount} ô trống nhưng đang khai báo ${answers.length} đáp án.`,
        itemIndex: i,
      };
    }
    for (let j = 0; j < answers.length; j++) {
      if (!answers[j]) {
        return { message: `Đáp án thứ ${j + 1} của câu ${i + 1} không được để trống.`, itemIndex: i };
      }
    }
  }
  return null;
};

const validateMemoryPairs = (items: any[]): GameValidationResult | null => {
  if (!isNonEmptyArray(items)) {
    return { message: 'Trò chơi Lật thẻ phải có ít nhất 4 cặp.' };
  }
  if (items.length < 4) {
    return { message: 'Trò chơi Lật thẻ phải có ít nhất 4 cặp để chơi hợp lý.' };
  }
  const side1Values: string[] = [];
  const side2Values: string[] = [];
  for (let i = 0; i < items.length; i++) {
    const it = items[i] || {};
    const s1 = normalizeText(it.side1);
    const s2 = normalizeText(it.side2);
    if (!s1 || !s2) {
      return { message: `Cặp số ${i + 1} chưa đủ cả hai mặt.`, itemIndex: i };
    }
    side1Values.push(s1);
    side2Values.push(s2);
  }
  const dup1 = findDuplicateText(side1Values);
  if (dup1) return { message: `Mặt 1 bị trùng: "${dup1}".` };
  const dup2 = findDuplicateText(side2Values);
  if (dup2) return { message: `Mặt 2 bị trùng: "${dup2}".` };
  return null;
};

const validateOrderingSequence = (sequence: any): GameValidationResult | null => {
  if (!sequence || typeof sequence !== 'object') {
    return { message: 'Trò chơi Sắp xếp thứ tự cần có dữ liệu các bước.' };
  }
  const items = Array.isArray(sequence.items) ? sequence.items : [];
  if (items.length < 3) {
    return { message: 'Cần ít nhất 3 bước để sắp xếp.' };
  }
  if (items.length > 12) {
    return { message: 'Quá nhiều bước (>12) sẽ khó chơi. Hãy gộp/giảm bớt.' };
  }
  const texts: string[] = [];
  for (let i = 0; i < items.length; i++) {
    const text = normalizeText(items[i]?.text);
    if (!text) {
      return { message: `Bước số ${i + 1} chưa có nội dung.`, itemIndex: i };
    }
    texts.push(text);
  }
  const dup = findDuplicateText(texts);
  if (dup) return { message: `Bước bị trùng nội dung: "${dup}".` };
  return null;
};

const validateWordSearch = (wordSearch: any): GameValidationResult | null => {
  if (!wordSearch || typeof wordSearch !== 'object') {
    return { message: 'Trò chơi Tìm từ ẩn cần có dữ liệu từ.' };
  }
  const words = Array.isArray(wordSearch.words) ? wordSearch.words : [];
  if (words.length < 4) {
    return { message: 'Cần ít nhất 4 từ để chơi Tìm từ ẩn.' };
  }
  if (words.length > 15) {
    return { message: 'Quá nhiều từ (>15) sẽ khó nhét vào bảng. Tối đa 15.' };
  }
  const seen = new Set<string>();
  for (let i = 0; i < words.length; i++) {
    const raw = normalizeText(words[i]?.word).toUpperCase();
    if (!raw) {
      return { message: `Từ số ${i + 1} chưa có nội dung.`, itemIndex: i };
    }
    if (raw.length < 2 || raw.length > 14) {
      return { message: `Từ "${raw}" phải có độ dài 2-14 ký tự.`, itemIndex: i };
    }
    if (/\s/.test(raw)) {
      return { message: `Từ "${raw}" không được chứa khoảng trắng.`, itemIndex: i };
    }
    if (!/^[A-Z0-9]+$/.test(raw)) {
      return { message: `Từ "${raw}" chỉ được chứa chữ A-Z và số (không dấu tiếng Việt).`, itemIndex: i };
    }
    if (seen.has(raw)) {
      return { message: `Từ "${raw}" bị trùng.`, itemIndex: i };
    }
    seen.add(raw);
  }
  return null;
};

const validateLetterMatchPairs = (pairs: any[]): GameValidationResult | null => {
  if (!isNonEmptyArray(pairs)) {
    return { message: 'Ghép hình/ghép chữ phải có ít nhất một cặp thẻ.' };
  }
  for (let i = 0; i < pairs.length; i++) {
    const p = pairs[i] || {};
    const promptText = normalizeText(p?.prompt?.text);
    if (!promptText) {
      return { message: `Thẻ gốc của cặp số ${i + 1} chưa có nội dung (chữ/số/ký hiệu).`, itemIndex: i };
    }
    const matches = Array.isArray(p.matches) ? p.matches : [];
    const validMatches = matches.filter((m: any) => normalizeText(m?.text) || normalizeText(m?.image));
    if (validMatches.length === 0) {
      return { message: `Cặp ghép số ${i + 1} cần ít nhất một đáp án (chữ hoặc ảnh).`, itemIndex: i };
    }
    for (let j = 0; j < matches.length; j++) {
      const match = matches[j] || {};
      const text = normalizeText(match.text);
      const image = normalizeText(match.image);
      if (!text && !image) {
        return {
          message: `Đáp án số ${j + 1} của cặp ghép số ${i + 1} cần có chữ hoặc ảnh.`,
          itemIndex: i,
        };
      }
    }
  }
  return null;
};

export function validateManualGameData(game: Game | null | undefined): GameValidationResult | null {
  if (!game) return { message: 'Không có dữ liệu trò chơi để lưu.' };

  if (!normalizeText(game.title)) {
    return { message: 'Tiêu đề trò chơi không được để trống.' };
  }
  if (!normalizeText(game.topic)) {
    return { message: 'Chủ đề trò chơi không được để trống.' };
  }
  if (!game.gameType) {
    return { message: 'Cần chọn loại trò chơi hợp lệ.' };
  }

  const content = game.content;
  if (!content || (typeof content === 'object' && !Array.isArray(content) && Object.keys(content).length === 0)) {
    return { message: 'Trò chơi chưa có nội dung. Hãy thêm dữ liệu trước khi lưu.' };
  }

  const type = game.gameType;

  if (QUESTION_BASED_GAME_TYPES.has(type)) {
    const questions = Array.isArray(content) ? content : content?.questions;
    return validateQuestionBased(questions);
  }

  if (type === GameType.GHEP_DOI_THONG_TIN) {
    const pairs = Array.isArray(content)
      ? content.map((item: any) => ({
          itemA: item?.itemA ?? item?.term ?? item?.left ?? '',
          itemB: item?.itemB ?? item?.definition ?? item?.right ?? '',
        }))
      : content?.matchingPairs;
    return validateMatchingPairs(pairs);
  }

  if (type === GameType.BINGO_KIEN_THUC) {
    const items = Array.isArray(content) ? content : content?.bingoItems;
    return validateBingoItems(items);
  }

  if (type === GameType.DONG_THOI_GIAN) {
    const events = Array.isArray(content) ? content : content?.events;
    return validateTimelineEvents(events);
  }

  if (type === GameType.O_CHU_BI_MAT || type === GameType.GIAI_MA_O_CHU) {
    if (Array.isArray(content)) {
      // Legacy: array of { word, clue }
      if (content.length === 0) return { message: 'Ô chữ phải có ít nhất một từ khóa.' };
      for (let i = 0; i < content.length; i++) {
        const item = content[i] || {};
        const word = normalizeText(item.word ?? item.answer);
        const clue = normalizeText(item.clue);
        if (!word || !clue) {
          return { message: `Từ khóa số ${i + 1} và gợi ý không được để trống.`, itemIndex: i };
        }
      }
      return null;
    }
    return validateCrossword(content);
  }

  if (SCENARIO_GAME_TYPES.has(type)) {
    const scenarios = Array.isArray(content) ? content : content?.scenarios;
    return validateScenarios(scenarios);
  }

  if (DEBATE_GAME_TYPES.has(type)) {
    const topics = Array.isArray(content) ? content : content?.debateTopics;
    return validateDebateTopics(topics);
  }

  if (type === GameType.BE_BAT_CHUOC) {
    const actions = Array.isArray(content) ? content : content?.actions;
    return validateActions(actions);
  }

  if (type === GameType.GHEP_HINH_GHEP_CHU) {
    const pairs = Array.isArray(content) ? content : content?.pairs;
    return validateLetterMatchPairs(pairs);
  }

  if (type === GameType.PHAN_LOAI_NHOM) {
    return validateClassification(content?.classification);
  }

  if (type === GameType.DUNG_SAI) {
    const items = Array.isArray(content) ? content : content?.trueFalseStatements;
    return validateTrueFalse(items);
  }

  if (type === GameType.DIEN_KHUYET) {
    const items = Array.isArray(content) ? content : content?.fillInBlanks;
    return validateFillInBlanks(items);
  }

  if (type === GameType.LAT_THE_TIM_CAP) {
    const items = Array.isArray(content) ? content : content?.memoryPairs;
    return validateMemoryPairs(items);
  }

  if (type === GameType.SAP_XEP_THU_TU) {
    return validateOrderingSequence(content?.orderingSequence);
  }

  if (type === GameType.TIM_TU_AN) {
    return validateWordSearch(content?.wordSearch);
  }

  // Fallback: nếu nội dung là object rỗng hoặc mảng rỗng
  if (Array.isArray(content) && content.length === 0) {
    return { message: 'Trò chơi chưa có nội dung. Hãy thêm dữ liệu trước khi lưu.' };
  }
  if (typeof content === 'object' && !Array.isArray(content)) {
    const hasAnyArray = Object.values(content).some(
      (value) => Array.isArray(value) && value.length > 0
    );
    if (!hasAnyArray) {
      return { message: 'Trò chơi chưa có nội dung. Hãy thêm dữ liệu trước khi lưu.' };
    }
  }

  return null;
}

export function supportsGameCsvImport(gameType: GameType): boolean {
  return (
    QUESTION_BASED_GAME_TYPES.has(gameType) ||
    SCENARIO_GAME_TYPES.has(gameType) ||
    DEBATE_GAME_TYPES.has(gameType) ||
    [
      GameType.GHEP_DOI_THONG_TIN,
      GameType.BINGO_KIEN_THUC,
      GameType.DONG_THOI_GIAN,
      GameType.O_CHU_BI_MAT,
      GameType.GIAI_MA_O_CHU,
      GameType.BE_BAT_CHUOC,
      GameType.GHEP_HINH_GHEP_CHU,
      GameType.PHAN_LOAI_NHOM,
      GameType.DUNG_SAI,
      GameType.DIEN_KHUYET,
      GameType.LAT_THE_TIM_CAP,
      GameType.SAP_XEP_THU_TU,
      GameType.TIM_TU_AN,
    ].includes(gameType)
  );
}

export function createBlankGameContent(gameType: GameType): any {
  if (QUESTION_BASED_GAME_TYPES.has(gameType)) {
    return {
      questions: [
        {
          question: '',
          options: ['', '', '', ''],
          answer: '',
          hint: '',
          image: '',
        },
      ],
    };
  }

  if (gameType === GameType.GHEP_DOI_THONG_TIN) {
    return {
      matchingPairs: [{ itemA: '', itemB: '', imageA: '', imageB: '' }],
    };
  }

  if (gameType === GameType.BINGO_KIEN_THUC) {
    return {
      bingoItems: [{ text: '', image: '' }],
    };
  }

  if (gameType === GameType.DONG_THOI_GIAN) {
    return {
      events: [{ title: '', year: '', description: '', image: '' }],
    };
  }

  if (gameType === GameType.O_CHU_BI_MAT || gameType === GameType.GIAI_MA_O_CHU) {
    return {
      crossword: {
        keyword: '',
        hint: '',
        image: '',
        clues: [{ clue: '', answer: '', keywordSlot: 1, image: '' }],
      },
    };
  }

  if (SCENARIO_GAME_TYPES.has(gameType)) {
    return {
      scenarios: [
        {
          title: '',
          context: '',
          problem: '',
          openness: 'Mở',
          difficulty: 'Trung bình',
          image: '',
          roles: [{ name: '', description: '', goal: '', emotion: '' }],
          tips: '',
          settings: { prepTime: 5, playTime: 5, feedbackTime: 5 },
        },
      ],
    };
  }

  if (DEBATE_GAME_TYPES.has(gameType)) {
    return {
      debateTopics: [
        {
          topic: '',
          type: 'Khẳng định',
          difficulty: 'Cụ thể',
          image: '',
          description: '',
          affirmativeTeam: { name: 'Đội Ủng hộ', goal: '' },
          negativeTeam: { name: 'Đội Phản đối', goal: '' },
        },
      ],
    };
  }

  if (gameType === GameType.BE_BAT_CHUOC) {
    return {
      actions: [{ title: '', type: 'Toàn thân', description: '', sound: '', image: '' }],
    };
  }

  if (gameType === GameType.GHEP_HINH_GHEP_CHU) {
    return {
      pairs: [
        {
          id: 1,
          prompt: { text: '', type: 'Chữ cái', image: '' },
          matches: [{ id: 2, text: '', image: '' }],
        },
      ],
    };
  }

  if (gameType === GameType.PHAN_LOAI_NHOM) {
    return {
      classification: {
        columns: [
          { id: 'A', label: 'Nhóm A', description: '' },
          { id: 'B', label: 'Nhóm B', description: '' },
        ],
        cards: [
          { text: '', correctColumn: 'A', explanation: '', image: '' },
          { text: '', correctColumn: 'B', explanation: '', image: '' },
        ],
      },
    };
  }

  if (gameType === GameType.DUNG_SAI) {
    return {
      trueFalseStatements: [
        { statement: '', isTrue: true, explanation: '', image: '' },
      ],
    };
  }

  if (gameType === GameType.DIEN_KHUYET) {
    return {
      fillInBlanks: [
        { sentence: '', answers: [''], wordBank: [], explanation: '' },
      ],
    };
  }

  if (gameType === GameType.LAT_THE_TIM_CAP) {
    return {
      memoryPairs: [
        { side1: '', side2: '', image1: '', image2: '', note: '' },
      ],
    };
  }

  if (gameType === GameType.SAP_XEP_THU_TU) {
    return {
      orderingSequence: {
        instruction: '',
        items: [
          { text: '', detail: '', image: '' },
        ],
      },
    };
  }

  if (gameType === GameType.TIM_TU_AN) {
    return {
      wordSearch: {
        words: [
          { word: '', clue: '' },
        ],
      },
    };
  }

  return {
    questions: [{ question: '', answer: '', options: ['', '', '', ''], hint: '', image: '' }],
  };
}

export function getGameImportTemplate(gameType: GameType): string {
  if (QUESTION_BASED_GAME_TYPES.has(gameType)) {
    return [
      'CAU_HOI\tDAP_AN_1\tDAP_AN_2\tDAP_AN_3\tDAP_AN_4\tDAP_AN_DUNG\tGOI_Y\tANH',
      'Hành tinh nào gần Mặt trời nhất?\tSao Thủy\tSao Kim\tTrái Đất\tSao Hỏa\tSao Thủy\tDùng cho vòng khởi động\t',
      'Ai là tác giả Truyện Kiều?\tNguyễn Du\tNguyễn Trãi\tXuân Diệu\tTố Hữu\tNguyễn Du\tCâu hỏi văn học cơ bản\t',
    ].join('\n');
  }

  switch (gameType) {
    case GameType.GHEP_DOI_THONG_TIN:
      return [
        'VAT_A\tVAT_B\tANH_A\tANH_B',
        'Thủ đô Việt Nam\tHà Nội\t\t',
        '2 + 2\t4\t\t',
      ].join('\n');
    case GameType.BINGO_KIEN_THUC:
      return ['NOI_DUNG\tANH', 'Quang hợp\t', 'Hô hấp\t'].join('\n');
    case GameType.DONG_THOI_GIAN:
      return ['NAM\tTIEU_DE\tMO_TA\tANH', '1945\tCách mạng Tháng Tám\tGiành chính quyền\t'].join('\n');
    case GameType.O_CHU_BI_MAT:
    case GameType.GIAI_MA_O_CHU:
      return [
        'TU_KHOA\tGOI_Y_TU_KHOA\tGOI_Y_HANG_NGANG\tDAP_AN\tVI_TRI_TU_KHOA\tANH',
        'VIETNAM\tĐất nước hình chữ S\tThủ đô của Việt Nam\tHANOI\t1\t',
        'VIETNAM\tĐất nước hình chữ S\tVịnh nổi tiếng ở Quảng Ninh\tHALONG\t3\t',
      ].join('\n');
    case GameType.BE_BAT_CHUOC:
      return ['TIEU_DE\tLOAI\tMO_TA\tAM_THANH\tANH', 'Chú thỏ\tToàn thân\tNhảy và giơ hai tay như tai thỏ\tBíp bíp\t'].join('\n');
    case GameType.GHEP_HINH_GHEP_CHU:
      return [
        'GOI_Y_CHINH\tLOAI_GOI_Y\tANH_GOI_Y\tDAP_AN_1\tANH_DAP_AN_1\tDAP_AN_2\tANH_DAP_AN_2',
        'A\tChữ cái\t\tQuả táo\thttps://example.com/apple.jpg\tMáy bay\thttps://example.com/plane.jpg',
      ].join('\n');
    case GameType.PHAN_LOAI_NHOM:
      return [
        'NOI_DUNG_THE\tCOT_DUNG\tGIAI_THICH\tANH',
        'Cá voi\tA\tThú có vú sống dưới nước\t',
        'Chim sẻ\tB\tLớp chim\t',
        'Cá heo\tA\tThú có vú\t',
        'Đại bàng\tB\tLớp chim\t',
        '# Cột A: Lớp Thú | Cột B: Lớp Chim (sửa dòng đầu khai báo cột)',
      ].join('\n');
    case GameType.DUNG_SAI:
      return [
        'KHANG_DINH\tDAP_AN\tGIAI_THICH\tANH',
        'Mặt trời là một ngôi sao.\tDUNG\tMặt trời là sao loại G2V.\t',
        'Loài dơi là loài chim.\tSAI\tDơi là động vật có vú, không phải chim.\t',
      ].join('\n');
    case GameType.DIEN_KHUYET:
      return [
        'CAU_DOAN\tDAP_AN\tNGAN_HANG_TU\tGIAI_THICH\tANH',
        'Quá trình ____ giúp cây xanh tạo ra ____ và ô-xy.\tquang hợp | glucose\tquang hợp,hô hấp,glucose,protein\tDùng ____ (4 gạch dưới) cho mỗi chỗ trống. Đáp án phân cách bởi " | ".\t',
      ].join('\n');
    case GameType.LAT_THE_TIM_CAP:
      return [
        'MAT_1\tMAT_2\tGHI_CHU\tANH_1\tANH_2',
        'Photosynthesis\tQuang hợp\t\t\t',
        'H₂O\tNước\t\t\t',
        'CO₂\tKhí cacbonic\t\t\t',
        'Cell\tTế bào\t\t\t',
      ].join('\n');
    case GameType.SAP_XEP_THU_TU:
      return [
        'BUOC\tCHI_TIET\tANH',
        'Đọc kỹ đề bài, tìm các từ khóa\tGạch chân các đại lượng quan trọng\t',
        'Tóm tắt đề bằng sơ đồ/bảng\tDùng sơ đồ đoạn thẳng nếu cần\t',
        'Lập kế hoạch giải\tLựa chọn phép tính phù hợp\t',
        'Thực hiện phép tính\tTrình bày rõ ràng\t',
        'Kiểm tra và kết luận\tThử lại bằng cách thay vào\t',
        '# Mẹo: nhập các bước theo ĐÚNG thứ tự, hệ thống sẽ xáo trộn khi chơi',
      ].join('\n');
    case GameType.TIM_TU_AN:
      return [
        'TU\tGOI_Y\tANH',
        'QUANGHOP\tQuá trình cây xanh tổng hợp chất hữu cơ\t',
        'AXIT\tDung dịch có pH < 7\t',
        'TEBAO\tĐơn vị cấu trúc của sinh vật\t',
        'OXY\tKhí cần thiết cho hô hấp\t',
        '# Lưu ý: từ phải VIẾT HOA, KHÔNG dấu, KHÔNG khoảng trắng',
      ].join('\n');
    default:
      if (SCENARIO_GAME_TYPES.has(gameType)) {
        return [
          'TIEU_DE\tBOI_CANH\tVAN_DE\tVAI_TRO_1\tVAI_TRO_2\tVAI_TRO_3\tLUU_Y\tANH',
          'Xung đột nhóm\tLàm việc nhóm\tMột bạn không tham gia\tNhóm trưởng\tBạn lười\tNgười hòa giải\tKhuyến khích học sinh đưa giải pháp\t',
        ].join('\n');
      }
      if (DEBATE_GAME_TYPES.has(gameType)) {
        return [
          'TIEU_DE\tLOAI\tMUC_DO\tMO_TA\tMUC_TIEU_UNG_HO\tMUC_TIEU_PHAN_DOI\tANH',
          'Học online tốt hơn học trực tiếp\tKhẳng định\tCụ thể\tSo sánh hai hình thức học\tChứng minh tính linh hoạt\tChứng minh tính tương tác trực tiếp\t',
        ].join('\n');
      }
      return '';
  }
}

export function validateGameImport(gameType: GameType, rows: string[][]): Record<number, string[]> {
  const errors: Record<number, string[]> = {};
  if (rows.length === 0) return errors;

  const clean = (value: unknown): string => String(value ?? '').trim().replace(/\s+/g, ' ');
  const norm = (value: unknown): string =>
    clean(value).toLowerCase().replace(/^\s*[a-d]\s*[\.\)\-:]\s*/, '').trim();
  const findDup = (values: string[]): boolean => {
    const seen = new Set<string>();
    for (const v of values) {
      if (!v) continue;
      if (seen.has(v)) return true;
      seen.add(v);
    }
    return false;
  };

  // Skip header row if it exists
  const isHeader = rows[0].some(cell =>
    ['cau_hoi', 'question', 'vat_a', 'itema', 'noi_dung', 'text', 'nam', 'year', 'tu_khoa', 'keyword', 'tieu_de', 'title', 'goi_y_chinh', 'prompt', 'topic'].includes(cell.trim().toLowerCase())
  );
  const dataRows = isHeader ? rows.slice(1) : rows;
  const offset = isHeader ? 1 : 0;

  dataRows.forEach((row, i) => {
    const lineErrors: string[] = [];
    const rowIndex = i + offset;

    const nonEmpty = row.filter((c) => clean(c) !== '').length;
    if (nonEmpty === 0) return;

    if (QUESTION_BASED_GAME_TYPES.has(gameType)) {
      const question = clean(row[0]);
      const options = [row[1], row[2], row[3], row[4]].map(clean);
      const populated = options.filter((o) => o !== '');
      const answer = clean(row[5]);
      if (!question) lineErrors.push('Thiếu câu hỏi');
      if (!answer) lineErrors.push('Thiếu đáp án đúng');
      if (populated.length > 0 && populated.length < 2) {
        lineErrors.push('Cần ít nhất 2 phương án (hoặc để trống tất cả để ra câu hỏi mở)');
      }
      if (populated.length >= 2) {
        if (findDup(populated.map(norm))) lineErrors.push('Phương án bị trùng nội dung');
        if (answer) {
          const na = norm(answer);
          const matched = populated.some((opt) => norm(opt) === na);
          if (!matched) lineErrors.push('Đáp án đúng không khớp phương án nào');
        }
      }
    } else if (gameType === GameType.GHEP_DOI_THONG_TIN) {
      const a = clean(row[0]);
      const b = clean(row[1]);
      if (!a || !b) lineErrors.push('Cặp ghép phải có đủ 2 vế');
      else if (norm(a) === norm(b)) lineErrors.push('Hai vế của cặp ghép đang trùng nhau');
    } else if (gameType === GameType.BINGO_KIEN_THUC) {
      if (!clean(row[0])) lineErrors.push('Thiếu nội dung ô Bingo');
    } else if (gameType === GameType.DONG_THOI_GIAN) {
      if (!clean(row[0])) lineErrors.push('Thiếu năm/thời điểm');
      if (!clean(row[1])) lineErrors.push('Thiếu tiêu đề sự kiện');
    } else if (gameType === GameType.O_CHU_BI_MAT || gameType === GameType.GIAI_MA_O_CHU) {
      const keyword = clean(row[0]);
      const clue = clean(row[2]);
      const answer = clean(row[3]);
      const slotRaw = clean(row[4]);
      if (!clue) lineErrors.push('Thiếu gợi ý hàng ngang');
      if (!answer) lineErrors.push('Thiếu đáp án hàng ngang');
      if (answer && /\s/.test(answer)) lineErrors.push('Đáp án hàng ngang không được chứa khoảng trắng');
      if (slotRaw) {
        const slot = Number(slotRaw);
        if (!Number.isFinite(slot) || slot < 1) {
          lineErrors.push('Vị trí từ khóa phải là số nguyên dương');
        } else if (answer && slot > answer.length) {
          lineErrors.push(`Vị trí từ khóa phải ≤ độ dài đáp án (${answer.length})`);
        }
        if (keyword && slot > 0 && answer) {
          const keywordChar = keyword.charAt(slot - 1);
          if (!keywordChar) {
            lineErrors.push('Vị trí từ khóa vượt quá độ dài từ khóa bí mật');
          }
        }
      }
    } else if (SCENARIO_GAME_TYPES.has(gameType)) {
      if (!clean(row[0])) lineErrors.push('Thiếu tiêu đề tình huống');
      if (!clean(row[2])) lineErrors.push('Thiếu vấn đề/mâu thuẫn');
    } else if (DEBATE_GAME_TYPES.has(gameType)) {
      if (!clean(row[0])) lineErrors.push('Thiếu chủ đề tranh biện');
    } else if (gameType === GameType.BE_BAT_CHUOC) {
      if (!clean(row[0])) lineErrors.push('Thiếu tiêu đề động tác');
    } else if (gameType === GameType.GHEP_HINH_GHEP_CHU) {
      if (!clean(row[0])) lineErrors.push('Thiếu gợi ý chính');
      const match1 = clean(row[3]);
      const match2 = clean(row[5]);
      if (!match1 && !match2) lineErrors.push('Cần ít nhất 1 đáp án ghép');
      if (match1 && match2 && norm(match1) === norm(match2)) {
        lineErrors.push('Hai đáp án ghép đang trùng nhau');
      }
    } else if (gameType === GameType.PHAN_LOAI_NHOM) {
      const text = clean(row[0]);
      const col = clean(row[1]);
      if (text.startsWith('#')) return; // dòng comment
      if (!text) lineErrors.push('Thiếu nội dung thẻ');
      if (!col) lineErrors.push('Thiếu cột đúng (A/B/C/D)');
      else if (!/^[A-D]$/i.test(col)) lineErrors.push('Cột đúng chỉ nhận A, B, C hoặc D');
    } else if (gameType === GameType.DUNG_SAI) {
      const stmt = clean(row[0]);
      const ans = clean(row[1]).toUpperCase();
      if (!stmt) lineErrors.push('Thiếu khẳng định');
      if (!ans) lineErrors.push('Thiếu đáp án DUNG/SAI');
      else if (!['DUNG', 'SAI', 'TRUE', 'FALSE', 'Đ', 'S'].includes(ans)) {
        lineErrors.push('Đáp án chỉ nhận DUNG hoặc SAI');
      }
    } else if (gameType === GameType.DIEN_KHUYET) {
      const sentence = clean(row[0]);
      const answers = clean(row[1]);
      if (!sentence) lineErrors.push('Thiếu nội dung câu');
      if (!answers) lineErrors.push('Thiếu đáp án');
      else {
        const blankCount = (sentence.match(/_{4,}/g) || []).length;
        const answerCount = answers.split('|').filter(s => s.trim() !== '').length;
        if (blankCount === 0) lineErrors.push('Câu phải có ít nhất 1 ô trống "____" (4 gạch dưới)');
        else if (blankCount !== answerCount) {
          lineErrors.push(`Câu có ${blankCount} ô trống nhưng nhập ${answerCount} đáp án`);
        }
      }
    } else if (gameType === GameType.LAT_THE_TIM_CAP) {
      const s1 = clean(row[0]);
      const s2 = clean(row[1]);
      if (!s1 || !s2) lineErrors.push('Cặp cần đủ cả hai mặt');
    } else if (gameType === GameType.SAP_XEP_THU_TU) {
      const step = clean(row[0]);
      if (step.startsWith('#')) return;
      if (!step) lineErrors.push('Thiếu nội dung bước');
    } else if (gameType === GameType.TIM_TU_AN) {
      const word = clean(row[0]).toUpperCase();
      if (word.startsWith('#')) return;
      if (!word) lineErrors.push('Thiếu từ');
      else if (!/^[A-Z0-9]+$/.test(word)) lineErrors.push('Từ chỉ chứa A-Z, 0-9 (không dấu, không khoảng trắng)');
      else if (word.length < 2 || word.length > 14) lineErrors.push('Độ dài từ 2-14 ký tự');
    }

    if (lineErrors.length > 0) {
      errors[rowIndex] = lineErrors;
    }
  });

  return errors;
}

export function parseGameImportText(gameType: GameType, rawText: string): ParsedGameImport {
  const { rows } = parseDelimitedText(rawText);
  if (rows.length === 0) {
    throw new Error('Chưa có dữ liệu để nhập. Hãy dán bảng từ Excel/CSV hoặc chọn tệp.');
  }

  if (QUESTION_BASED_GAME_TYPES.has(gameType)) {
    const items = rowsToObjects(rows, ['question', 'option1', 'option2', 'option3', 'option4', 'answer', 'hint', 'image'], {
      cau_hoi: 'question',
      dap_an_1: 'option1',
      dap_an_2: 'option2',
      dap_an_3: 'option3',
      dap_an_4: 'option4',
      dap_an_dung: 'answer',
      goi_y: 'hint',
      anh: 'image',
    }).map((row) => ({
      question: row.question || '',
      options: [row.option1, row.option2, row.option3, row.option4].filter(Boolean),
      answer: row.answer || '',
      hint: row.hint || '',
      image: row.image || '',
    })).filter((row) => row.question || row.answer || row.options.length > 0);

    if (items.length === 0) throw new Error('Không đọc được câu hỏi nào từ dữ liệu nhập.');
    return { content: { questions: items }, importedCount: items.length, summaryLabel: 'câu hỏi' };
  }

  switch (gameType) {
    case GameType.GHEP_DOI_THONG_TIN: {
      const pairs = rowsToObjects(rows, ['itemA', 'itemB', 'imageA', 'imageB'], {
        vat_a: 'itemA',
        vat_b: 'itemB',
        anh_a: 'imageA',
        anh_b: 'imageB',
      }).map((row) => ({
        itemA: row.itemA || '',
        itemB: row.itemB || '',
        imageA: row.imageA || '',
        imageB: row.imageB || '',
      })).filter((row) => row.itemA || row.itemB);
      if (pairs.length === 0) throw new Error('Không đọc được cặp thông tin nào từ dữ liệu nhập.');
      return { content: { matchingPairs: pairs }, importedCount: pairs.length, summaryLabel: 'cặp ghép' };
    }
    case GameType.BINGO_KIEN_THUC: {
      const items = rowsToObjects(rows, ['text', 'image'], {
        noi_dung: 'text',
        anh: 'image',
      }).map((row) => ({
        text: row.text || '',
        image: row.image || '',
      })).filter((row) => row.text);
      if (items.length === 0) throw new Error('Không đọc được ô Bingo nào từ dữ liệu nhập.');
      return { content: { bingoItems: items }, importedCount: items.length, summaryLabel: 'ô Bingo' };
    }
    case GameType.DONG_THOI_GIAN: {
      const items = rowsToObjects(rows, ['year', 'title', 'description', 'image'], {
        nam: 'year',
        tieu_de: 'title',
        mo_ta: 'description',
        anh: 'image',
      }).map((row) => ({
        year: row.year || '',
        title: row.title || '',
        description: row.description || '',
        image: row.image || '',
      })).filter((row) => row.year || row.title);
      if (items.length === 0) throw new Error('Không đọc được mốc thời gian nào từ dữ liệu nhập.');
      return { content: { events: items }, importedCount: items.length, summaryLabel: 'mốc thời gian' };
    }
    case GameType.O_CHU_BI_MAT:
    case GameType.GIAI_MA_O_CHU: {
      const entries = rowsToObjects(rows, ['keyword', 'hint', 'clue', 'answer', 'keywordSlot', 'image'], {
        tu_khoa: 'keyword',
        goi_y_tu_khoa: 'hint',
        goi_y_hang_ngang: 'clue',
        dap_an: 'answer',
        vi_tri_tu_khoa: 'keywordSlot',
        anh: 'image',
      });
      const keyword = entries[0]?.keyword || '';
      const hint = entries[0]?.hint || '';
      const clues = entries.map((row, index) => ({
        clue: row.clue || '',
        answer: row.answer || '',
        keywordSlot: Number(row.keywordSlot || index + 1) || index + 1,
        image: row.image || '',
      })).filter((row) => row.clue || row.answer);
      if (!keyword && clues.length === 0) throw new Error('Không đọc được dữ liệu ô chữ từ bảng nhập.');
      return {
        content: {
          crossword: {
            keyword,
            hint,
            image: '',
            clues,
          },
        },
        importedCount: clues.length,
        summaryLabel: 'gợi ý ô chữ',
      };
    }
    case GameType.BE_BAT_CHUOC: {
      const actions = rowsToObjects(rows, ['title', 'type', 'description', 'sound', 'image'], {
        tieu_de: 'title',
        loai: 'type',
        mo_ta: 'description',
        am_thanh: 'sound',
        anh: 'image',
      }).map((row) => ({
        title: row.title || '',
        type: row.type || 'Toàn thân',
        description: row.description || '',
        sound: row.sound || '',
        image: row.image || '',
      })).filter((row) => row.title || row.description);
      if (actions.length === 0) throw new Error('Không đọc được động tác nào từ dữ liệu nhập.');
      return { content: { actions }, importedCount: actions.length, summaryLabel: 'động tác' };
    }
    case GameType.PHAN_LOAI_NHOM: {
      const items = rowsToObjects(rows, ['text', 'correctColumn', 'explanation', 'image'], {
        noi_dung_the: 'text',
        cot_dung: 'correctColumn',
        giai_thich: 'explanation',
        anh: 'image',
      }).map((row) => ({
        text: row.text || '',
        correctColumn: (row.correctColumn || '').toUpperCase().trim(),
        explanation: row.explanation || '',
        image: row.image || '',
      })).filter((row) => row.text && !row.text.startsWith('#'));
      if (items.length === 0) throw new Error('Không đọc được thẻ phân loại nào.');
      const usedColumns = Array.from(new Set(items.map(i => i.correctColumn).filter(Boolean)));
      const columns = usedColumns.sort().map(id => ({
        id,
        label: `Nhóm ${id}`,
        description: '',
      }));
      return {
        content: { classification: { columns, cards: items } },
        importedCount: items.length,
        summaryLabel: 'thẻ phân loại',
      };
    }
    case GameType.DUNG_SAI: {
      const items = rowsToObjects(rows, ['statement', 'isTrue', 'explanation', 'image'], {
        khang_dinh: 'statement',
        dap_an: 'isTrue',
        giai_thich: 'explanation',
        anh: 'image',
      }).map((row) => ({
        statement: row.statement || '',
        isTrue: ['DUNG', 'TRUE', 'Đ'].includes(String(row.isTrue || '').toUpperCase().trim()),
        explanation: row.explanation || '',
        image: row.image || '',
      })).filter((row) => row.statement);
      if (items.length === 0) throw new Error('Không đọc được khẳng định Đúng/Sai nào.');
      return { content: { trueFalseStatements: items }, importedCount: items.length, summaryLabel: 'khẳng định' };
    }
    case GameType.DIEN_KHUYET: {
      const items = rowsToObjects(rows, ['sentence', 'answers', 'wordBank', 'explanation', 'image'], {
        cau_doan: 'sentence',
        dap_an: 'answers',
        ngan_hang_tu: 'wordBank',
        giai_thich: 'explanation',
        anh: 'image',
      }).map((row) => ({
        sentence: row.sentence || '',
        answers: String(row.answers || '').split('|').map(s => s.trim()).filter(Boolean),
        wordBank: String(row.wordBank || '').split(/[,;]/).map(s => s.trim()).filter(Boolean),
        explanation: row.explanation || '',
        image: row.image || '',
      })).filter((row) => row.sentence && row.answers.length > 0);
      if (items.length === 0) throw new Error('Không đọc được câu điền khuyết nào.');
      return { content: { fillInBlanks: items }, importedCount: items.length, summaryLabel: 'câu điền khuyết' };
    }
    case GameType.LAT_THE_TIM_CAP: {
      const items = rowsToObjects(rows, ['side1', 'side2', 'note', 'image1', 'image2'], {
        mat_1: 'side1',
        mat_2: 'side2',
        ghi_chu: 'note',
        anh_1: 'image1',
        anh_2: 'image2',
      }).map((row) => ({
        side1: row.side1 || '',
        side2: row.side2 || '',
        image1: row.image1 || '',
        image2: row.image2 || '',
        note: row.note || '',
      })).filter((row) => row.side1 && row.side2);
      if (items.length === 0) throw new Error('Không đọc được cặp thẻ nào.');
      return { content: { memoryPairs: items }, importedCount: items.length, summaryLabel: 'cặp thẻ' };
    }
    case GameType.TIM_TU_AN: {
      const items = rowsToObjects(rows, ['word', 'clue', 'image'], {
        tu: 'word',
        goi_y: 'clue',
        anh: 'image',
      }).map((row) => ({
        word: String(row.word || '').toUpperCase().trim(),
        clue: row.clue || '',
        image: row.image || '',
      })).filter((row) => row.word && !row.word.startsWith('#') && /^[A-Z0-9]+$/.test(row.word));
      if (items.length === 0) throw new Error('Không đọc được từ ẩn nào (chú ý: chữ hoa, không dấu, không khoảng trắng).');
      return {
        content: { wordSearch: { words: items } },
        importedCount: items.length,
        summaryLabel: 'từ ẩn',
      };
    }
    case GameType.GHEP_HINH_GHEP_CHU: {
      const pairs = rowsToObjects(rows, ['prompt', 'promptType', 'promptImage', 'match1', 'match1Image', 'match2', 'match2Image'], {
        goi_y_chinh: 'prompt',
        loai_goi_y: 'promptType',
        anh_goi_y: 'promptImage',
        dap_an_1: 'match1',
        anh_dap_an_1: 'match1Image',
        dap_an_2: 'match2',
        anh_dap_an_2: 'match2Image',
      }).map((row, index) => ({
        id: Date.now() + index,
        prompt: {
          text: row.prompt || '',
          type: row.promptType || 'Chữ cái',
          image: row.promptImage || '',
        },
        matches: [1, 2]
          .map((slot) => ({
            id: Date.now() + index * 10 + slot,
            text: row[`match${slot}`] || '',
            image: row[`match${slot}Image`] || '',
          }))
          .filter((item) => item.text || item.image),
      })).filter((row) => row.prompt.text || row.matches.length > 0);
      if (pairs.length === 0) throw new Error('Không đọc được cặp ghép chữ/hình nào từ dữ liệu nhập.');
      return { content: { pairs }, importedCount: pairs.length, summaryLabel: 'thẻ ghép' };
    }
    default:
      if (SCENARIO_GAME_TYPES.has(gameType)) {
        const scenarios = rowsToObjects(rows, ['title', 'context', 'problem', 'role1', 'role2', 'role3', 'tips', 'image'], {
          tieu_de: 'title',
          boi_canh: 'context',
          van_de: 'problem',
          vai_tro_1: 'role1',
          vai_tro_2: 'role2',
          vai_tro_3: 'role3',
          luu_y: 'tips',
          anh: 'image',
        }).map((row) => ({
          title: row.title || '',
          context: row.context || '',
          problem: row.problem || '',
          openness: 'Mở',
          difficulty: 'Trung bình',
          image: row.image || '',
          roles: ['role1', 'role2', 'role3']
            .map((key) => row[key] || '')
            .filter(Boolean)
            .map((name) => ({ name, description: '', goal: '', emotion: '' })),
          tips: row.tips || '',
          settings: { prepTime: 5, playTime: 5, feedbackTime: 5 },
        })).filter((row) => row.title || row.problem);
        if (scenarios.length === 0) throw new Error('Không đọc được tình huống nào từ dữ liệu nhập.');
        return { content: { scenarios }, importedCount: scenarios.length, summaryLabel: 'tình huống' };
      }

      if (DEBATE_GAME_TYPES.has(gameType)) {
        const debateTopics = rowsToObjects(rows, ['topic', 'type', 'difficulty', 'description', 'affirmativeGoal', 'negativeGoal', 'image'], {
          tieu_de: 'topic',
          loai: 'type',
          muc_do: 'difficulty',
          mo_ta: 'description',
          muc_tieu_ung_ho: 'affirmativeGoal',
          muc_tieu_phan_doi: 'negativeGoal',
          anh: 'image',
        }).map((row) => ({
          topic: row.topic || '',
          type: row.type || 'Khẳng định',
          difficulty: row.difficulty || 'Cụ thể',
          image: row.image || '',
          description: row.description || '',
          affirmativeTeam: { name: 'Đội Ủng hộ', goal: row.affirmativeGoal || '' },
          negativeTeam: { name: 'Đội Phản đối', goal: row.negativeGoal || '' },
        })).filter((row) => row.topic || row.description);
        if (debateTopics.length === 0) throw new Error('Không đọc được chủ đề tranh biện nào từ dữ liệu nhập.');
        return { content: { debateTopics }, importedCount: debateTopics.length, summaryLabel: 'chủ đề tranh biện' };
      }
      break;
  }

  throw new Error('Loại trò chơi này chưa hỗ trợ import CSV ở chế độ thủ công.');
}
